import { getZendeskBaseUrl } from '../config/runtimeConfig';

/**
 * Sanitize a redirect URL to prevent open redirect attacks.
 * Allows:
 *   - Relative paths (e.g. /app, /client/dashboard)
 *   - Any *.vida.com or vida.com URL
 * Rejects everything else and returns the fallback.
 */
export function sanitizeRedirectUrl(url: string | undefined, fallback: string): string {
  if (!url) return fallback;

  // Allow relative paths — but NOT protocol-relative (//evil.com)
  if (url.startsWith('/') && !url.startsWith('//')) return url;

  try {
    const parsed = new URL(url);
    if (parsed.hostname === 'vida.com' || parsed.hostname.endsWith('.vida.com')) {
      return url;
    }
  } catch {
    // Malformed URL — fall through to fallback
  }

  return fallback;
}

/**
 * Recognize a return_to URL that points at the Zendesk-backed support portal,
 * so the post-login flow can branch into the Zendesk Web SSO handshake at
 * {ZENDESK_URL}/access/jwt instead of the regular redirect path.
 *
 * Matches the production host (support.vida.com) and any subdomain (e.g.
 * staging.support.vida.com). The leading-dot check prevents accidental
 * matches on lookalike hosts like evil-support.vida.com.
 */
export function isZendeskReturnTo(url: string | undefined): boolean {
  if (!url) return false;
  try {
    const { hostname } = new URL(url);
    return hostname === 'support.vida.com' || hostname.endsWith('.support.vida.com');
  } catch {
    return false;
  }
}

/**
 * Recognize any https URL on the Vida Zendesk account, including the raw
 * account host (vida95.zendesk.com in prod) that Zendesk itself puts in
 * ?return_to when it bounces an unauthenticated request.
 *
 * Deliberately broader than isZendeskReturnTo, and used for a different
 * decision: isZendeskReturnTo gates the *member* JWT handoff, this gates
 * whether we offer the employee Okta option. Both member and employee
 * traffic arrives on the same hosts, so this can never be used to route
 * automatically — only to decide what to show.
 */
export function isZendeskUrl(url: string | undefined): boolean {
  if (!url) return false;
  try {
    const { protocol, hostname } = new URL(url);
    if (protocol !== 'https:') return false;
    return isZendeskReturnTo(url) || hostname === new URL(getZendeskBaseUrl()).hostname;
  } catch {
    return false;
  }
}
